import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';
import './Profile.css';

const LANGUAGES   = ['Arabic','French','German','English','Italian','Spanish'];
const COUNTRIES   = ['Germany','Tunisia','France','Belgium','Netherlands','Switzerland','Austria','Sweden','Canada','United States','United Kingdom','Other'];
const GERMAN_CITIES   = ['Berlin','Hamburg','Munich','Frankfurt','Cologne','Stuttgart','Düsseldorf','Dortmund','Essen','Leipzig','Bremen','Dresden','Hanover','Nuremberg'];
const TUNISIAN_CITIES = ['Tunis','Sfax','Sousse','Monastir','Bizerte','Gabès','Ariana','Gafsa','Kairouan','Nabeul'];

// ── Tab pill ──────────────────────────────────────────────────────────────
function TabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{ padding:'8px 18px', borderRadius:'100px', fontSize:'13px', fontWeight: active ? '700' : '400', background: active ? 'var(--red)' : 'rgba(255,255,255,0.05)', border: active ? '1px solid var(--red)' : '1px solid rgba(255,255,255,0.08)', color: active ? '#fff' : 'var(--olive)', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}>
      {children}
    </button>
  );
}

// ── Section header ────────────────────────────────────────────────────────
function SectionH({ title, sub }) {
  return (
    <div style={{ marginBottom:'20px' }}>
      <h3 style={{ fontSize:'16px', fontWeight:'700', color:'var(--sand)', marginBottom:'4px' }}>{title}</h3>
      {sub && <p style={{ fontSize:'13px', color:'var(--olive)' }}>{sub}</p>}
    </div>
  );
}

// ── Info row ──────────────────────────────────────────────────────────────
function InfoRow({ label, value, accent }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:'14px' }}>
      <span style={{ color:'var(--olive)' }}>{label}</span>
      <span style={{ color: accent || 'var(--sand)', fontWeight:'600' }}>{value}</span>
    </div>
  );
}

// ── Star display ──────────────────────────────────────────────────────────
function Stars({ rating }) {
  const r = Math.round(rating || 0);
  return <span style={{ color:'#F5A623' }}>{'★'.repeat(r)}{'☆'.repeat(5 - r)}</span>;
}

export default function Profile() {
  const { profile, updateProfile, user } = useAuth();
  const { t } = useLang();
  const fileRef = useRef();

  const [tab,     setTab]     = useState('info');
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');
  const [uploading, setUploading] = useState(false);

  // Stats
  const [stats,   setStats]   = useState({ trips:0, bookings:0, delivered:0, totalVolume:0 });
  const [reviews, setReviews] = useState([]);

  const [form, setForm] = useState({
    full_name:      profile?.full_name     || '',
    phone:          profile?.phone         || '',
    city:           profile?.city          || '',
    country:        profile?.country       || 'Germany',
    country_origin: profile?.country_origin|| '',
    bio:            profile?.bio           || '',
    role:           profile?.role          || 'sender',
    languages:      profile?.languages     || [],
    whatsapp:       profile?.whatsapp      || '',
    avatar_url:     profile?.avatar_url    || '',
  });

  useEffect(() => {
    if (user) { fetchStats(); fetchReviews(); }
  }, [user]);

  async function fetchStats() {
    const [tripsRes, bookingsRes] = await Promise.all([
      supabase.from('trips').select('id', { count:'exact', head:true }).eq('transporter_id', user.id),
      supabase.from('bookings').select('id, status, total_price').eq('sender_id', user.id),
    ]);
    const b = bookingsRes.data || [];
    setStats({
      trips:       tripsRes.count || 0,
      bookings:    b.length,
      delivered:   b.filter(x => x.status === 'delivered').length,
      totalVolume: b.filter(x => ['paid','cash'].includes(x.payment_status || '') || x.status === 'delivered').reduce((s,x) => s + (x.total_price || 0), 0),
    });
  }

  async function fetchReviews() {
    const { data } = await supabase
      .from('reviews')
      .select('*, reviewer:profiles!reviewer_id(full_name, avatar_url)')
      .eq('reviewee_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);
    setReviews(data || []);
  }

  function update(f, v) { setForm(prev => ({ ...prev, [f]: v })); }
  function toggleLang(lang) { update('languages', form.languages.includes(lang) ? form.languages.filter(l => l !== lang) : [...form.languages, lang]); }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB.'); return; }
    setUploading(true); setError('');
    const ext  = file.name.split('.').pop();
    const path = `${user.id}/avatar.${ext}`;
    const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    if (upErr) { setError(upErr.message); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
    update('avatar_url', publicUrl + '?t=' + Date.now());
    setUploading(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError('');
    const result = await updateProfile({
      full_name:      form.full_name,
      phone:          form.phone,
      city:           form.city,
      country:        form.country,
      country_origin: form.country_origin || null,
      bio:            form.bio,
      role:           form.role,
      languages:      form.languages,
      whatsapp:       form.whatsapp || null,
      avatar_url:     form.avatar_url || null,
    });
    setSaving(false);
    // Check for duplicate phone/whatsapp
    const errMsg = result?.error?.message || '';
    if (errMsg.includes('profiles_phone_unique') || errMsg.includes('duplicate key')) {
      setError('This phone number is already linked to another Transportily account. Please use a different number.');
      return;
    }
    if (errMsg) { setError(errMsg); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const roles = [
    { val:'sender',      icon:'📦', label: t('auth.iAmSender') },
    { val:'transporter', icon:'✈️', label: t('auth.iAmTransporter') },
    { val:'both',        icon:'🔄', label: t('auth.iAmBoth') },
  ];

  const cities = form.country === 'Tunisia' ? TUNISIAN_CITIES : form.country === 'Germany' ? GERMAN_CITIES : [];

  return (
    <div className="page profile-page">
      <div className="container">

        {/* ── HERO CARD ── */}
        <div style={{ display:'flex', gap:'24px', alignItems:'flex-start', marginBottom:'32px', flexWrap:'wrap' }}>

          {/* Avatar */}
          <div style={{ position:'relative', flexShrink:0 }}>
            <div style={{ width:'96px', height:'96px', borderRadius:'50%', overflow:'hidden', border:'3px solid rgba(232,52,26,0.4)', cursor:'pointer' }} onClick={() => fileRef.current?.click()}>
              {form.avatar_url
                ? <img src={form.avatar_url} alt="avatar" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <div style={{ width:'100%', height:'100%', background:'var(--red)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'36px', fontWeight:'800', color:'#fff', fontFamily:'Syne' }}>
                    {form.full_name?.[0] || '?'}
                  </div>
              }
            </div>
            <button type="button" onClick={() => fileRef.current?.click()}
              style={{ position:'absolute', bottom:'2px', right:'2px', width:'28px', height:'28px', borderRadius:'50%', background:'var(--red)', border:'2px solid var(--background)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px' }}>
              {uploading ? '⏳' : '📷'}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleAvatarChange} />
          </div>

          {/* Info */}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'6px' }}>
              <h1 style={{ fontSize:'24px', fontFamily:'Syne', fontWeight:'800', letterSpacing:'-0.5px', margin:0 }}>
                {form.full_name || 'Your Name'}
              </h1>
              {profile?.is_verified && (
                <span style={{ background:'rgba(76,175,125,0.12)', border:'1px solid rgba(76,175,125,0.3)', color:'#4CAF7D', borderRadius:'100px', padding:'2px 10px', fontSize:'12px', fontWeight:'700' }}>✓ Verified</span>
              )}
            </div>
            <div style={{ color:'var(--olive)', fontSize:'13px', marginBottom:'8px' }}>
              {[form.city, form.country].filter(Boolean).join(', ')}
              {form.languages?.length > 0 && ` · ${form.languages.join(', ')}`}
            </div>
            {/* Stats row */}
            <div style={{ display:'flex', gap:'20px', flexWrap:'wrap' }}>
              {[
                { val: profile?.rating?.toFixed(1) || '—', label:'Rating' },
                { val: profile?.total_reviews || 0,         label:'Reviews' },
                { val: stats.trips,                         label:'Trips posted' },
                { val: stats.delivered,                     label:'Deliveries' },
              ].map(s => (
                <div key={s.label} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'18px', fontWeight:'800', fontFamily:'Syne', color:'var(--sand)', letterSpacing:'-0.5px' }}>{s.val}</div>
                  <div style={{ fontSize:'10px', color:'var(--olive)', textTransform:'uppercase', letterSpacing:'0.5px', marginTop:'1px' }}>{s.label}</div>
                </div>
              ))}
            </div>
            {form.bio && <p style={{ fontSize:'13px', color:'rgba(245,237,216,0.6)', marginTop:'10px', lineHeight:'1.5', maxWidth:'420px' }}>{form.bio}</p>}
          </div>
        </div>

        {/* ── TABS ── */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'24px', flexWrap:'wrap' }}>
          <TabBtn active={tab==='info'}     onClick={() => setTab('info')}>Personal info</TabBtn>
          <TabBtn active={tab==='stats'}    onClick={() => setTab('stats')}>Statistics</TabBtn>
          <TabBtn active={tab==='reviews'}  onClick={() => setTab('reviews')}>Reviews ({reviews.length})</TabBtn>
          <TabBtn active={tab==='security'} onClick={() => setTab('security')}>Security</TabBtn>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom:'16px' }}>{error}</div>}
        {saved  && <div className="alert alert-success" style={{ marginBottom:'16px' }}>✓ Profile saved</div>}

        {/* ── TAB: PERSONAL INFO ── */}
        {tab === 'info' && (
          <form className="card" onSubmit={handleSave} style={{ maxWidth:'600px' }}>
            <SectionH title="Personal information" />

            <div className="form-group">
              <label>{t('profile.fullNameLabel')}</label>
              <input type="text" value={form.full_name} onChange={e => update('full_name', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Role</label>
              <div className="role-selector">
                {roles.map(r => (
                  <button key={r.val} type="button" className={`role-btn-sm ${form.role===r.val ? 'active' : ''}`} onClick={() => update('role', r.val)}>
                    <span>{r.icon}</span> {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>{t('profile.phoneLabel')}</label>
              <PhoneInput value={form.phone} onChange={v => update('phone', v)} />
            </div>

            <div className="form-group">
              <label>WhatsApp number <span style={{fontSize:'12px', fontWeight:'400', color:'var(--olive)'}}>— optional, if different from phone</span></label>
              <PhoneInput value={form.whatsapp} onChange={v => update('whatsapp', v)} />
              <span style={{fontSize:'11px', color:'var(--olive)'}}>Senders and transporters often prefer WhatsApp coordination</span>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
              <div className="form-group">
                <label>Country you live in</label>
                <select value={form.country} onChange={e => { update('country', e.target.value); update('city', ''); }}>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Country of origin</label>
                <select value={form.country_origin} onChange={e => update('country_origin', e.target.value)}>
                  <option value="">Not specified</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>{t('profile.cityLabel')}</label>
              {cities.length > 0 ? (
                <select value={form.city} onChange={e => update('city', e.target.value)}>
                  <option value="">Select city</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <AddressAutocomplete value={form.city} onChange={v => update('city', v)} placeholder="Your city" type="city" />
              )}
            </div>

            <div className="form-group">
              <label>Languages spoken</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'4px' }}>
                {LANGUAGES.map(lang => (
                  <button key={lang} type="button" onClick={() => toggleLang(lang)}
                    style={{ padding:'6px 14px', borderRadius:'100px', fontSize:'13px', border:`1px solid ${form.languages?.includes(lang) ? 'var(--red)' : 'rgba(255,255,255,0.1)'}`, background: form.languages?.includes(lang) ? 'rgba(232,52,26,0.12)' : 'rgba(255,255,255,0.04)', color: form.languages?.includes(lang) ? 'var(--sand)' : 'var(--olive)', cursor:'pointer', fontFamily:'inherit' }}>
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Bio <span style={{fontSize:'12px', fontWeight:'400', color:'var(--olive)'}}>— optional</span></label>
              <textarea rows={3} placeholder={t('profile.bioPlaceholder')} value={form.bio} onChange={e => update('bio', e.target.value)} maxLength={500} />
              <span style={{fontSize:'11px', color:'var(--olive)'}}>{form.bio.length}/500</span>
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('profile.savingBtn') : t('profile.saveBtn')}
            </button>
          </form>
        )}

        {/* ── TAB: STATS ── */}
        {tab === 'stats' && (
          <div style={{ maxWidth:'600px' }}>
            <div className="card" style={{ marginBottom:'16px' }}>
              <SectionH title="Your activity" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'12px' }}>
                {[
                  { icon:'✈️', val: stats.trips,    label:'Trips posted',  show: true },
                  { icon:'📦', val: stats.bookings,  label:'Total bookings', show: true },
                  { icon:'✅', val: stats.delivered, label:'Deliveries',    show: true },
                  { icon:'💶', val: `€${stats.totalVolume.toFixed(2)}`, label:'Total volume', show: true },
                  { icon:'⭐', val: profile?.rating?.toFixed(2) || '—', label:'Average rating', show: true },
                  { icon:'💬', val: reviews.length, label:'Reviews received', show: true },
                ].map(s => (
                  <div key={s.label} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'14px', padding:'16px' }}>
                    <div style={{ fontSize:'22px', marginBottom:'8px' }}>{s.icon}</div>
                    <div style={{ fontSize:'22px', fontFamily:'Syne', fontWeight:'800', color:'var(--sand)', letterSpacing:'-0.5px' }}>{s.val}</div>
                    <div style={{ fontSize:'11px', color:'var(--olive)', marginTop:'2px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Verification status */}
            <div className="card">
              <SectionH title="Verification status" />
              <InfoRow label="Identity verification" value={profile?.is_verified ? '✓ Approved' : profile?.verification_status === 'pending' ? '⏳ Under review' : '✗ Not submitted'} accent={profile?.is_verified ? '#4CAF7D' : profile?.verification_status === 'pending' ? '#E8C547' : 'var(--red)'} />
              <InfoRow label="Email" value={user?.email} />
              <InfoRow label="Member since" value={user?.created_at ? format(new Date(user.created_at), 'MMMM yyyy') : '—'} />
              {!profile?.is_verified && (
                <div style={{ marginTop:'16px', padding:'12px 14px', background:'rgba(232,197,71,0.08)', border:'1px solid rgba(232,197,71,0.2)', borderRadius:'10px', fontSize:'13px', color:'rgba(245,237,216,0.6)' }}>
                  <strong style={{ color:'#E8C547' }}>Get verified</strong> — Upload your ID to get the ✓ Verified badge. Verified transporters get up to 3× more booking requests.{' '}
                  <a href="/dashboard?tab=verification" style={{ color:'var(--sand)', textDecoration:'underline' }}>Start verification →</a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB: REVIEWS ── */}
        {tab === 'reviews' && (
          <div style={{ maxWidth:'600px' }}>
            {reviews.length === 0 ? (
              <div className="card" style={{ textAlign:'center', padding:'40px' }}>
                <div style={{ fontSize:'40px', marginBottom:'12px' }}>⭐</div>
                <h3>No reviews yet</h3>
                <p style={{ color:'var(--olive)', fontSize:'14px' }}>Reviews appear here after completed bookings.</p>
              </div>
            ) : (
              <div className="card">
                <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'20px' }}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'36px', fontFamily:'Syne', fontWeight:'800', color:'var(--sand)' }}>{profile?.rating?.toFixed(1) || '—'}</div>
                    <Stars rating={profile?.rating} />
                    <div style={{ fontSize:'12px', color:'var(--olive)', marginTop:'4px' }}>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</div>
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                  {reviews.map(r => (
                    <div key={r.id} style={{ paddingBottom:'20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                          <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'var(--red)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'700', color:'#fff' }}>
                            {r.reviewer?.full_name?.[0] || '?'}
                          </div>
                          <div>
                            <div style={{ fontSize:'14px', fontWeight:'600' }}>{r.reviewer?.full_name || 'Anonymous'}</div>
                            <div style={{ fontSize:'11px', color:'var(--olive)' }}>{format(new Date(r.created_at), 'MMM d, yyyy')}</div>
                          </div>
                        </div>
                        <div>
                          <Stars rating={r.rating} />
                          <span style={{ fontSize:'12px', color:'var(--olive)', marginLeft:'6px' }}>{r.rating}/5</span>
                        </div>
                      </div>
                      {r.comment && (
                        <p style={{ fontSize:'14px', color:'rgba(245,237,216,0.7)', margin:0, lineHeight:'1.5' }}>"{r.comment}"</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: SECURITY ── */}
        {tab === 'security' && (
          <div style={{ maxWidth:'480px' }}>
            <div className="card" style={{ marginBottom:'16px' }}>
              <SectionH title="Account security" />
              <InfoRow label="Email" value={user?.email} />
              <InfoRow label="Provider" value={user?.app_metadata?.provider === 'google' ? '🔵 Google' : user?.app_metadata?.provider === 'facebook' ? '🔷 Facebook' : '📧 Email & password'} />

              {(!user?.app_metadata?.provider || user?.app_metadata?.provider === 'email') && (
                <div style={{ marginTop:'16px' }}>
                  <a href="/reset-password" style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 16px', color:'var(--sand)', fontSize:'13px', fontWeight:'600', textDecoration:'none' }}>
                    🔑 Change password →
                  </a>
                </div>
              )}
            </div>

            <div className="card">
              <SectionH title="Connected accounts" sub="Link your social accounts for faster sign-in" />
              {[
                { provider:'google',   icon:'🔵', name:'Google' },
                { provider:'facebook', icon:'🔷', name:'Facebook' },
              ].map(p => {
                const connected = user?.app_metadata?.provider === p.provider ||
                  user?.app_metadata?.providers?.includes(p.provider);
                return (
                  <div key={p.provider} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                      <span style={{ fontSize:'20px' }}>{p.icon}</span>
                      <span style={{ fontSize:'14px', fontWeight:'600', color:'var(--sand)' }}>{p.name}</span>
                    </div>
                    {connected ? (
                      <span style={{ fontSize:'12px', color:'#4CAF7D', fontWeight:'700' }}>✓ Connected</span>
                    ) : (
                      <button type="button"
                        onClick={async () => {
                          await supabase.auth.linkIdentity({ provider: p.provider });
                        }}
                        style={{ fontSize:'12px', padding:'6px 14px', borderRadius:'8px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'var(--sand)', cursor:'pointer', fontFamily:'inherit' }}>
                        Connect
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}